import { useTu } from "@src/appV2/i18n/useTu";
import { formatLongDate, formatTime } from "@clipboard-health/date-time";
import { Box, Stack } from "@mui/material";
import { type Money } from "@src/appV2/lib/Money/types";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { type TimeSlot } from "@src/appV2/redesign/Shift/types";
import type { UseModalState } from "@src/lib/ui-react";
import { useEffect, useState } from "react";
import { Avatar } from "../../../components/Avatar";
import { DialogFooter } from "../../../components/DialogFooter";
import { Divider } from "../../../components/Divider";
import { ResponsiveDialog } from "../../../components/ResponsiveDialog";
import { Text } from "../../../components/Text";
import { TimeSlotAvatar } from "../../../Shift/TimeSlotAvatar";
import { ChargeRateControls } from "../../ShiftDetails/components/ChargeRateControls";
import { minorToMajor } from "../../ShiftDetails/components/chargeRateUtils";
import { type ChargeConstraints } from "./hooks/useInviteChargeRate";

interface EditInviteChargeRateDialogProps {
  modalState: UseModalState;
  baseChargeRate: Money;
  chargeConstraints: ChargeConstraints;
  currentBoost: number | undefined;
  onSave: (boost: number | undefined) => void;
  professionalName: string;
  professionalAvatarUrl?: string;
  qualificationDisplayName: string;
  startTime: Date;
  endTime: Date;
  timeSlot: TimeSlot;
  timezone: string;
}

const WORKER_BONUS_PERCENTAGE = 0.75;

export function EditInviteChargeRateDialog(props: EditInviteChargeRateDialogProps) {
  const { tu } = useTu("schedule");

  const {
    modalState,
    baseChargeRate,
    chargeConstraints,
    currentBoost,
    onSave,
    professionalName,
    professionalAvatarUrl,
    qualificationDisplayName,
    startTime,
    endTime,
    timeSlot,
    timezone,
  } = props;

  const baseRateInDollars = minorToMajor(baseChargeRate.amountInMinorUnits);

  const maxBoostInDollars =
    minorToMajor(chargeConstraints.max.amountInMinorUnits) - baseRateInDollars;

  const [boost, setBoost] = useState(currentBoost ?? 0);

  useEffect(() => {
    if (modalState.modalIsOpen) {
      setBoost(currentBoost ?? 0);
    }
  }, [modalState.modalIsOpen, currentBoost]);

  const workerBonusInDollars =
    boost > 0 ? Math.floor(boost * WORKER_BONUS_PERCENTAGE * 100) / 100 : 0;

  const handleSave = () => {
    onSave(boost > 0 ? boost : undefined);
    modalState.closeModal();
  };

  return (
    <ResponsiveDialog
      showCloseButton
      modalState={modalState}
      title={tu("Boost this shift")}
      width={450}
      footerContent={
        <DialogFooter
          primaryAction={{ label: "Save", onClick: handleSave }}
          secondaryAction={{ label: "Cancel", onClick: modalState.closeModal }}
        />
      }
    >
      <Stack spacing={5}>
        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.palette.border?.base}`,
            borderRadius: theme.borderRadius?.xLarge,
            padding: 4,
          })}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
            <Stack direction="row" alignItems="center" spacing={3}>
              <Avatar alt={professionalName} size="medium" src={professionalAvatarUrl} />
              <Box>
                <Text semibold variant="body2">
                  {professionalName}
                </Text>
                <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                  {qualificationDisplayName}
                </Text>
              </Box>
            </Stack>
            <Box sx={{ textAlign: "right" }}>
              <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                {tu("Hourly bonus")}
              </Text>
              <Text semibold variant="body2">
                {formatDollarsAsUsd(workerBonusInDollars)}/hr
              </Text>
            </Box>
          </Stack>

          <Divider sx={{ marginY: 4, marginX: -4, width: "auto" }} />

          <Stack direction="row" alignItems="center" spacing={3}>
            <TimeSlotAvatar timeSlot={timeSlot} />
            <Box>
              <Text semibold variant="body2">
                {`${formatTime(startTime, { timeZone: timezone })} - ${formatTime(endTime, { timeZone: timezone })}`}
              </Text>
              <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                {formatLongDate(startTime, { timeZone: timezone })}
              </Text>
            </Box>
          </Stack>
        </Box>

        <ChargeRateControls
          maxRate={maxBoostInDollars}
          maxRateDisplayValue={baseRateInDollars + boost}
          maxRateLabel="Boosted rate"
          minRate={0}
          minRateDisplayValue={baseRateInDollars}
          minRateLabel="Contract rate"
          rate={boost}
          title={tu("Extra hourly charge")}
          onRateChange={setBoost}
        />
      </Stack>
    </ResponsiveDialog>
  );
}
